import { useEffect, useState } from 'react';
import { api, formatPKR } from '../api';
import PageHeader from '../components/PageHeader';

import { CHARITY_LABEL, CHARITY_SHARE_PERCENT, MAIN_OWNERS } from '../utils/constants';

export default function Owners() {
  const [owners, setOwners] = useState([]);
  const [charity, setCharity] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [form, setForm] = useState({ ownerName: '', amount: '', notes: '' });
  const [investmentForm, setInvestmentForm] = useState({ ownerName: '', amount: '' });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [investmentMessage, setInvestmentMessage] = useState('');
  const [savingInvestment, setSavingInvestment] = useState(false);

  const load = () =>
    api.getOwners().then((data) => {
      setOwners(data.owners);
      setCharity(data.charity ?? null);
      setWithdrawals(data.withdrawals);
    });

  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, []);

  const partnerOwners = owners.filter((o) => MAIN_OWNERS.includes(o.name));
  const legacyCharity = owners.find((o) => o.name === CHARITY_LABEL);
  const charityInfo =
    charity ??
    (legacyCharity
      ? {
          label: CHARITY_LABEL,
          sharePercent: legacyCharity.sharePercent ?? CHARITY_SHARE_PERCENT,
          shareAmount: legacyCharity.shareAmount ?? legacyCharity.totalEarned ?? 0,
        }
      : null);

  const handleWithdrawal = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      await api.recordWithdrawal({
        ownerName: form.ownerName,
        amount: Number(form.amount),
        notes: form.notes,
        paymentMethod: 'Bank Transfer',
        status: 'Completed',
        approvedBy: 'Admin',
      });
      setMessage('Withdrawal recorded');
      setForm({ ownerName: '', amount: '', notes: '' });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleInvestment = async (e) => {
    e.preventDefault();
    setError('');
    setInvestmentMessage('');
    const ownerName = investmentForm.ownerName;
    const amount = Number(investmentForm.amount);
    setSavingInvestment(true);
    try {
      await api.updateOwnerInvestment(ownerName, amount);
      setOwners((prev) =>
        prev.map((o) => (o.name === ownerName ? { ...o, investmentAmount: amount } : o))
      );
      setInvestmentMessage('Investment amount updated');
      setInvestmentForm({ ownerName: '', amount: '' });
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingInvestment(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Owners"
        subtitle={`3 partners at 32% each · ${CHARITY_SHARE_PERCENT}% charity (${CHARITY_LABEL})`}
      />
      {charityInfo && (
        <div className="card mb-8 max-w-lg">
          <h3 className="font-serif font-medium text-lumora-dark-wood text-sm">
            {charityInfo.label} — Charity Allocation
          </h3>
          <p className="text-xs text-lumora-text/50 mt-1">
            Not an owner — {charityInfo.sharePercent}% of distributable profit set aside for charity
          </p>
          <p className="text-lg font-semibold text-lumora-wood mt-3">
            {formatPKR(charityInfo.shareAmount)}
          </p>
        </div>
      )}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
        {partnerOwners.map((o) => (
          <div key={o.name} className="card">
            <h3 className="font-serif font-medium text-lumora-dark-wood text-sm">{o.name}</h3>
            <p className="text-xs text-lumora-text/50 mt-1">{o.sharePercent}% share</p>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-lumora-text/60">Investment</span>
                <span className="font-medium text-lumora-wood">{formatPKR(o.investmentAmount ?? 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-lumora-text/60">Share Earned</span>
                <span>{formatPKR(o.shareAmount ?? o.totalEarned)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-lumora-text/60">Withdrawn</span>
                <span>{formatPKR(o.totalWithdrawn)}</span>
              </div>
              <div className="flex justify-between font-semibold text-lumora-wood">
                <span>Remaining</span>
                <span>{formatPKR((o.shareAmount ?? o.totalEarned) - o.totalWithdrawn)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={handleInvestment} className="card max-w-lg mb-8 space-y-4">
        <h3 className="font-serif font-medium text-lumora-dark-wood">Set Investment Amount</h3>
        <p className="text-sm text-lumora-text/60">
          Record how much each owner has invested in the business.
        </p>
        <select
          value={investmentForm.ownerName}
          onChange={(e) => setInvestmentForm({ ...investmentForm, ownerName: e.target.value })}
          className="input"
          required
        >
          <option value="">Select Owner</option>
          {partnerOwners.map((o) => (
            <option key={o.name} value={o.name}>
              {o.name}
            </option>
          ))}
        </select>
        <input
          type="number"
          min="0"
          step="1"
          placeholder="Investment amount (PKR)"
          value={investmentForm.amount}
          onChange={(e) => setInvestmentForm({ ...investmentForm, amount: e.target.value })}
          className="input"
          required
        />
        {investmentMessage && <p className="text-green-700 text-sm">{investmentMessage}</p>}
        <button type="submit" className="btn-primary" disabled={savingInvestment}>
          {savingInvestment ? 'Saving…' : 'Save Investment'}
        </button>
      </form>

      <form onSubmit={handleWithdrawal} className="card max-w-lg mb-8 space-y-4">
        <h3 className="font-serif font-medium text-lumora-dark-wood">Record Withdrawal</h3>
        <select
          value={form.ownerName}
          onChange={(e) => setForm({ ...form, ownerName: e.target.value })}
          className="input"
          required
        >
          <option value="">Select Owner</option>
          {partnerOwners.map((o) => (
            <option key={o.name} value={o.name}>
              {o.name}
            </option>
          ))}
        </select>
        <input
          type="number"
          min="1"
          placeholder="Amount"
          value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })}
          className="input"
          required
        />
        <input
          placeholder="Notes (optional)"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          className="input"
        />
        {error && <p className="text-red-600 text-sm">{error}</p>}
        {message && <p className="text-green-700 text-sm">{message}</p>}
        <button type="submit" className="btn-primary">
          Record Withdrawal
        </button>
      </form>

      <div className="card">
        <h3 className="font-serif font-medium text-lumora-dark-wood mb-4">Withdrawal History</h3>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Owner</th>
                <th>Amount</th>
                <th>Date</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {withdrawals.map((w) => (
                <tr key={w.id}>
                  <td>{w.id}</td>
                  <td>{w.ownerName}</td>
                  <td>{formatPKR(w.amount)}</td>
                  <td>{w.date}</td>
                  <td>{w.notes}</td>
                </tr>
              ))}
              {withdrawals.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-lumora-text/50 py-8">
                    No withdrawals yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
